// Making the day digestible: short names, compact rooms and day parts.
// Pure helpers (no I/O) shared by the web app's Focus view; unit-tested in test/digest.test.ts.

const MODULE_RE = /\b([A-Z]{4}\d{4})\b/;

const SESSION_KINDS: [RegExp, string][] = [
  [/\b(test|exam|quiz|assessment)\b/i, 'Test'],
  [/\blab(oratory)?s?\b|\bpractical\b/i, 'Lab'],
  [/\btut(orial)?s?\b|\bproblem class\b/i, 'Tutorial'],
  [/\bworkshop\b/i, 'Workshop'],
  [/\bseminar\b/i, 'Seminar'],
  [/\bdrop[\s-]?in\b/i, 'Drop-in'],
  [/\b(lecture|lec)\b|\bL\d*\b/, 'Lecture'],
];

export interface ShortTitle {
  /** what to show big, e.g. "MATH1054 Lecture" */
  title: string;
  /** optional second line, e.g. the module's name */
  detail: string | null;
}

const tidy = (s: string) => s.replace(/\s+/g, ' ').replace(/[\s,;:–-]+$/, '').trim();
const stripParens = (s: string) => tidy(s.replace(/\s*[([][^)\]]*[)\]]/g, ''));

/** Long task titles → the gist: "Statics Tutorial Sheet 3 (beams…) for the week 7 tutorial" → "Statics Tutorial Sheet 3". */
export function gist(title: string, max = 42): string {
  let t = stripParens(title);
  for (const cut of [': ', ' – ', ' - ', ' before ', ' ahead of ', ' for the ', ' from ', ' so that ', ' because ']) {
    const i = t.toLowerCase().indexOf(cut.toLowerCase());
    if (i >= 15) t = t.slice(0, i);
  }
  t = tidy(t);
  if (t.length > max) t = `${tidy(t.slice(0, t.lastIndexOf(' ', max) > 20 ? t.lastIndexOf(' ', max) : max))}…`;
  return t || title.slice(0, max);
}

/**
 * Timetable titles are long ("MATH1054 (s1) L2 - Maths for Eng & Environment (Aero,Mar,Med)").
 * Show "MATH1054 Lecture" with the module name as detail; other things get their gist.
 */
export function shortTitle(item: { title: string; kind: string; category?: string | null }): ShortTitle {
  if (item.kind === 'event' && item.category === 'uni') {
    const code = MODULE_RE.exec(item.title)?.[1];
    if (code) {
      const kind = SESSION_KINDS.find(([re]) => re.test(item.title.replace(code, '')))?.[1] ?? 'Session';
      const afterDash = item.title.split(/\s[-–]\s/).slice(1).join(' - ');
      const name = afterDash ? stripParens(afterDash) : null;
      return { title: `${code} ${kind}`, detail: name && name.toLowerCase() !== kind.toLowerCase() ? name : null };
    }
  }
  if (item.kind === 'deadline') return { title: gist(item.title.replace(/^Due:\s*/i, 'Due: ')), detail: null };
  if (item.kind === 'task' || item.kind === 'event') {
    const g = gist(item.title);
    return { title: g, detail: null };
  }
  return { title: item.title, detail: null };
}

/** "35 / 1001" → "35/1001"; "42 / SUSU / The Cinema" → "42/SUSU · The Cinema". */
export function roomLabel(location: string | null | undefined): string | null {
  if (!location) return null;
  const parts = location.split('/').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return location.trim();
  const code = parts.filter((p) => /^[\w.-]{1,6}$/.test(p));
  const words = parts.filter((p) => !/^[\w.-]{1,6}$/.test(p));
  return [code.join('/'), ...words].filter(Boolean).join(' · ');
}

export type DayPart = 'morning' | 'afternoon' | 'evening';
export const DAY_PART_LABEL: Record<DayPart, string> = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };

/** By local hour: before 12 morning, 12–17 afternoon, 17+ evening. */
export function dayPart(localHour: number): DayPart {
  return localHour < 12 ? 'morning' : localHour < 17 ? 'afternoon' : 'evening';
}
